import { Activity } from "@portal/api/comment";
import { Button } from "@portal/BaseComponents/Button/AntButton";
import { SkeletonInput } from "@portal/BaseComponents/Skeleton/AntSkeleton";
import { ToolTip } from "@portal/BaseComponents/ToolTip/AntTooltip";
import { usePrivileges } from "@portal/hooks/use-privilleges";
import useOpen from "@portal/hooks/useOpen";
import { Icon } from "@portal/lib/icons/icons";
import { useAddComment, useGetCommentsQuery } from "@portal/queries/comment";
import { Comment_UI_DTO } from "@portal/types/entities/comment";
import { GrowFlex, HideWrapper } from "@sinecycle/growcomponents";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect } from "react";
import { ActivitiesActionButtonProps } from "../type";
import { CommentContent, CommentFooter } from "./CommentFooter/CommentFooter";
import { CommentBox } from "./CommentForm/Box/CommentBox";
import { CommentModal } from "./CommentForm/Modal/CommentModal";
import { CommentList } from "./CommentList/CommentList";

const t = {
  title: "Comment",
};

interface CommentProps extends ActivitiesActionButtonProps {
  type?: "MODAL" | "BOX" | "DRAWER";
  id?: string;
  activity: Activity;
}
function Comment(props: CommentProps) {
  const {
    title = t.title,
    buttonProps,
    iconOnly,
    showToolTip,
    hideIcon,
    type = "MODAL",
    id,
    activity,
    onOpenChange,
  } = props;
  const { open, onClose, toggleOpen } = useOpen({ open: false });
  const { data: comments, isLoading } = useGetCommentsQuery({
    activity,
    params: id,
  });

  const { getCommentPrivileges } = usePrivileges();
  const queryClient = useQueryClient();
  const canCreateComment = getCommentPrivileges("COMMENT_CREATE");
  const canViewComment = getCommentPrivileges("COMMENT_LIST_VIEW");
  useEffect(() => {
    if (!comments || !comments.length) {
      const event = new CustomEvent("trigger-focus");
      document.dispatchEvent(event);
    }
  }, [comments]);
  const postComment = useAddComment({ activity, params: id });
  function handleClose() {
    onOpenChange?.(false);
    onClose();
  }

  function handleOpen() {
    onOpenChange?.(true);
    toggleOpen();
  }

  function handleSuccess(value: Comment_UI_DTO) {
    const currentData = queryClient.getQueriesData<Array<Comment_UI_DTO>>([
      `${activity}-comments-${id}`,
    ]);
    if (!currentData.length) return;
    const [, previousData] = currentData[0];
    if (!previousData) return;
    const newComments = [...previousData, value];
    queryClient.setQueriesData<Array<Comment_UI_DTO>>(
      [`${activity}-comments-${id}`],
      newComments
    );
  }
  function handleSave(value: CommentContent) {
    postComment
      .mutateAsync({
        private: value.is_private,
        comments: value.comment,
        file_upload_ids: value.file_upload_id,
      })
      .then(handleSuccess);
  }
  const CommentIcon = hideIcon ? null : (
    <Icon icon={["far", "comment-alt-lines"]} />
  );

  const CommentButton = (
    <ToolTip open={showToolTip ? undefined : false}>
      <Button icon={CommentIcon} {...buttonProps} onClick={handleOpen}>
        {iconOnly ? null : title}
      </Button>
    </ToolTip>
  );

  const Modal = (
    <>
      {CommentButton}
      <CommentModal open={open} onClose={onClose} />
    </>
  );

  const Box = open ? <CommentBox onClose={handleClose} /> : CommentButton;

  const Drawer = (
    <GrowFlex vertical space="8" style={{ width: "100%" }}>
      <HideWrapper hide={!canCreateComment}>
        {isLoading && !comments ? (
          <SkeletonInput style={{ width: "100%", height: "100px" }} />
        ) : (
          <CommentFooter handleSave={handleSave} />
        )}
      </HideWrapper>

      <HideWrapper hide={!canViewComment}>
        <CommentList list={comments ?? []} loading={postComment.isLoading} />
      </HideWrapper>
    </GrowFlex>
  );

  return <>{type === "MODAL" ? Modal : type === "DRAWER" ? Drawer : Box}</>;
}

export { Comment };
